import { isLeavePhysicalStoreName } from "../storeBrand";
import { STORE_BRANCH_MAP, DN_STORE_BRANCH_MAP, LEAVE_BRANCH_ALIASES } from "./storeMaps";
import { isActiveStaffMember } from "./userDisplay";

/** Resolve leave store label from emp.store / Foxline branch (PG + DN). */
export function resolveEmployeePgStore(emp, records) {
  if (!emp) return "";
  const storeLabel = String(emp.store || emp.store_name || "").trim();
  if (isLeavePhysicalStoreName(storeLabel, records)) return storeLabel;

  const branch = String(emp.branch_name || "").trim();
  if (branch) {
    const alias = LEAVE_BRANCH_ALIASES[branch];
    if (alias) return alias;
    const matches = [];
    for (const [storeId, branchName] of Object.entries(STORE_BRANCH_MAP)) {
      if (branch === branchName) matches.push(storeId);
    }
    for (const [storeId, branchName] of Object.entries(DN_STORE_BRANCH_MAP)) {
      if (branch === branchName) matches.push(storeId);
    }
    if (matches.length === 1) return matches[0];
    // Shared Foxline branches (e.g. Δράκου): prefer explicit store field via homeStore.
  }
  return homeStoreForEmployee(emp);
}

export function employeeAtPgStore(emp, storeLabel, records) {
  if (!storeLabel || storeLabel === "All Stores") return true;
  return resolveEmployeePgStore(emp, records) === storeLabel;
}

export function leaveRequestInStoreScope(req, allowedStores, storeFilter) {
  if (!req) return false;
  const storeId = String(req.store_id || "").trim();
  if (allowedStores && allowedStores.length > 0 && !allowedStores.includes(storeId)) {
    return false;
  }
  if (storeFilter && storeFilter !== "All Stores") {
    return storeId === storeFilter;
  }
  return true;
}

export function isSameLeaveEmployee(a, b) {
  if (!a || !b) return false;
  if (a.id != null && b.id != null && String(a.id) === String(b.id)) return true;
  const codeA = String(a.employee_code ?? a.employeeCode ?? "").trim();
  const codeB = String(b.employee_code ?? b.employeeCode ?? "").trim();
  if (codeA && codeB && codeA === codeB) return true;
  return String(a.name || "").trim().toUpperCase() === String(b.name || "").trim().toUpperCase();
}

/** Store manager roster: match store label, branch_name, or STORE_BRANCH_MAP resolution. */
export function employeeMatchesManagerStore(emp, managerStore, records) {
  if (!emp || !managerStore) return false;
  const ms = String(managerStore).trim();
  if (!ms) return false;
  if (String(emp.store || "").trim() === ms) return true;
  const branch = String(emp.branch_name || "").trim();
  if (branch === ms) return true;
  const expectedBranch = STORE_BRANCH_MAP[ms];
  if (expectedBranch && branch === expectedBranch) return true;
  return resolveEmployeePgStore(emp, records) === ms;
}

export function storeManagerLeaveEntitlements(allUsers, managerUser, records) {
  const managerStore = managerUser?.store;
  if (!managerStore) return managerUser ? [managerUser] : [];
  const active = (allUsers || []).filter(isActiveStaffMember);
  let pool = active.filter((emp) => employeeMatchesManagerStore(emp, managerStore, records));
  if (managerUser && !pool.some((emp) => isSameLeaveEmployee(emp, managerUser))) {
    pool = [...pool, managerUser];
  }
  return pool;
}

export function homeStoreForEmployee(emp) {
  if (!emp) return "";
  const storeLabel = String(emp.store || emp.store_name || "").trim();
  if (storeLabel) return storeLabel;
  const branch = String(emp.branch_name || "").trim();
  if (!branch) return "";
  const alias = LEAVE_BRANCH_ALIASES[branch];
  if (alias) return alias;
  for (const [storeId, branchName] of Object.entries(STORE_BRANCH_MAP)) {
    if (branchName === branch) return storeId;
  }
  for (const [storeId, branchName] of Object.entries(DN_STORE_BRANCH_MAP)) {
    if (branchName === branch) return storeId;
  }
  return "";
}

/** Employee's Foxline home store differs from the schedule builder store (loan/borrow flow). */
export function isCrossBranchEmployee(emp, currentStore) {
  if (!emp || !currentStore) return false;
  const home = homeStoreForEmployee(emp);
  // Use store label only — branch_name strings in Foxline often don't match STORE_BRANCH_MAP
  // exactly (e.g. Skoufa staff on Omirou branch text), which falsely blocked home-store scheduling.
  return Boolean(home && home !== currentStore);
}